/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

const createItem = (yearsCount = 3) => ({
  stt: "",
  content: "",
  totalDifference: "",
  allocationPeriod: "",
  yearlyAllocations: Array(yearsCount).fill(""),
});

const createGroup = (yearsCount = 3) => ({
  jointVentureName: "",
  items: [createItem(yearsCount)],
  yearlyTotals: Array(yearsCount).fill(0),
});

export function InvestmentJointVentureDifferentialAllocationRecordInfo({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const { yearsHeader = [], groups = [] } = values;

  // Sync yearsHeader length with item allocations
  const handleYearsHeaderChange = (val) => {
    const years = val.split(",").map((y) => y.trim()).filter(y => y !== "");
    setFieldValue("yearsHeader", years);

    // Update all items to match new years count
    const newGroups = groups.map((g) => ({
      ...g,
      items: g.items.map((item) => {
        const newAllocations = Array(years.length).fill("");
        item.yearlyAllocations.forEach((v, i) => {
          if (i < years.length) newAllocations[i] = v;
        });
        return { ...item, yearlyAllocations: newAllocations };
      }),
      yearlyTotals: Array(years.length).fill(0),
    }));
    setFieldValue("groups", newGroups);
  };

  const addGroup = () => {
    setFieldValue("groups", [...groups, createGroup(yearsHeader.length)]);
  };

  const removeGroup = (gIndex) => {
    if (groups.length <= 1) return;
    setFieldValue("groups", groups.filter((_, i) => i !== gIndex));
  };

  const addItem = (gIndex) => {
    const newGroups = [...groups];
    newGroups[gIndex].items.push(createItem(yearsHeader.length));
    setFieldValue("groups", newGroups);
  };

  const removeItem = (gIndex, iIndex) => {
    const newGroups = [...groups];
    if (newGroups[gIndex].items.length <= 1) return;
    newGroups[gIndex].items = newGroups[gIndex].items.filter((_, i) => i !== iIndex);
    setFieldValue("groups", newGroups);
  };

  const handleChangeItem = (gIndex, iIndex, field, val) => {
    const newGroups = [...groups];
    newGroups[gIndex].items[iIndex][field] = val;
    setFieldValue("groups", newGroups);
  };

  const handleChangeAllocation = (gIndex, iIndex, yearIndex, val) => {
    const newGroups = [...groups];
    newGroups[gIndex].items[iIndex].yearlyAllocations[yearIndex] = val;
    setFieldValue("groups", newGroups);
  };

  const num = (v) => (v && v !== "" && !isNaN(v) ? parseFloat(v) : 0);

  // Auto-calculate yearly totals for each group
  useEffect(() => {
    const newGroups = groups.map((g) => {
      const totals = Array(yearsHeader.length).fill(0);
      g.items.forEach((item) => {
        (item.yearlyAllocations || []).forEach((val, yIdx) => {
          if (yIdx < totals.length) totals[yIdx] += num(val);
        });
      });
      return { ...g, yearlyTotals: totals };
    });

    const totalsChanged = JSON.stringify(newGroups.map(g => g.yearlyTotals)) !== JSON.stringify(groups.map(g => g.yearlyTotals));
    if (totalsChanged) {
      setFieldValue("groups", newGroups);
    }
  }, [JSON.stringify(groups.map(g => g.items.map(i => i.yearlyAllocations)))]);

  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "20px", backgroundColor: "#fff", fontSize: "14px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "14px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "14px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    titleBox: { textAlign: "center", margin: "20px 0" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle", backgroundColor: "#f9f9f9" },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableRight: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "16px" }
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div>Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.unit} onChange={(e) => setFieldValue("unit", e.target.value)} /></div>
          <div>Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.address} onChange={(e) => setFieldValue("address", e.target.value)} /></div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S42a-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "12px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h3 style={{ margin: 0, textTransform: "uppercase" }}>SỔ THEO DÕI PHÂN BỔ CÁC KHOẢN CHÊNH LỆCH PHÁT SINH KHI MUA KHOẢN ĐẦU TƯ VÀO CÔNG TY LIÊN DOANH</h3>
        <div style={{ marginTop: 10 }}>
          Danh sách năm (cách nhau bởi dấu phẩy):{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "200px", textAlign: "center" }}
            value={yearsHeader.join(", ")}
            onChange={(e) => handleYearsHeaderChange(e.target.value)}
            placeholder="Ví dụ: 2024, 2025, 2026"
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: "60px" }} rowSpan={2}>STT</th>
              <th style={{ ...s.th, width: "250px" }} rowSpan={2}>Nội dung phân bổ</th>
              <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Tổng số chênh lệch (1)</th>
              <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Thời gian khấu hao (phân bổ) (2)</th>
              <th style={s.th} colSpan={yearsHeader.length}>Năm</th>
              {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={2}>#</th>}
            </tr>
            <tr>
              {yearsHeader.map((year, idx) => (
                <th key={idx} style={s.th}>{year}</th>
              ))}
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              {yearsHeader.map((_, idx) => (
                <th key={idx} style={s.th}>{idx + 3}</th>
              ))}
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {(groups || []).map((group, gIdx) => (
              <React.Fragment key={gIdx}>
                {/* Group Joint Venture Header */}
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <td style={{ ...s.td, textAlign: "center", fontWeight: "bold" }}>{gIdx + 1}</td>
                  <td colSpan={3 + yearsHeader.length} style={{ border: "1px solid #000", padding: "0 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <input
                        disabled={containView}
                        style={{ ...s.inputTable, fontWeight: "bold", width: "80%" }}
                        placeholder="Nhập tên Công ty liên doanh..."
                        value={group.jointVentureName}
                        onChange={(e) => {
                          const newGroups = [...groups];
                          newGroups[gIdx].jointVentureName = e.target.value;
                          setFieldValue("groups", newGroups);
                        }}
                      />
                      {!containView && (
                        <button type="button" style={s.removeBtn} onClick={() => removeGroup(gIdx)}>×</button>
                      )}
                    </div>
                  </td>
                  {!containView && <td style={s.td}></td>}
                </tr>

                {/* Group Items */}
                {(group.items || []).map((item, iIdx) => (
                  <tr key={iIdx}>
                    <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.stt} onChange={(e) => handleChangeItem(gIdx, iIdx, "stt", e.target.value)} /></td>
                    <td style={s.td}><input disabled={containView} style={s.inputTable} value={item.content} onChange={(e) => handleChangeItem(gIdx, iIdx, "content", e.target.value)} /></td>
                    <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.totalDifference} onChange={(e) => handleChangeItem(gIdx, iIdx, "totalDifference", e.target.value)} /></td>
                    <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.allocationPeriod} onChange={(e) => handleChangeItem(gIdx, iIdx, "allocationPeriod", e.target.value)} /></td>
                    {(item.yearlyAllocations || []).map((_, yIdx) => (
                      <td key={yIdx} style={s.td}>
                        <input
                          disabled={containView}
                          type="number"
                          style={s.inputTableRight}
                          value={item.yearlyAllocations[yIdx]}
                          onChange={(e) => handleChangeAllocation(gIdx, iIdx, yIdx, e.target.value)}
                        />
                      </td>
                    ))}
                    {!containView && (
                      <td style={{ ...s.td, textAlign: "center" }}>
                        <button type="button" style={s.removeBtn} onClick={() => removeItem(gIdx, iIdx)}>–</button>
                      </td>
                    )}
                  </tr>
                ))}

                {/* Group Footer (Cộng) */}
                <tr style={{ backgroundColor: "#fdfdfd", fontWeight: "bold" }}>
                  <td style={s.td}></td>
                  <td style={{ ...s.td, padding: "0 8px", fontStyle: "italic" }}>Cộng số phân bổ hàng năm</td>
                  <td style={s.td}></td>
                  <td style={s.td}></td>
                  {yearsHeader.map((_, yIdx) => (
                    <td key={yIdx} style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(group.yearlyTotals[yIdx])}</td>
                  ))}
                  {!containView && <td style={s.td}></td>}
                </tr>

                {!containView && (
                  <tr>
                    <td colSpan={5 + yearsHeader.length} style={{ textAlign: "center", padding: "4px" }}>
                      <SoftBox mt={3} textAlign="center">
                        <SoftButton variant="outlined" color="dark" onClick={() => addItem(gIdx)}>
                          + Thêm dòng chi tiết cho nhóm này
                        </SoftButton>
                      </SoftBox>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {!containView && (
          <SoftBox mt={2} textAlign="left">
            <SoftButton variant="outlined" color="dark" onClick={addGroup}>+ Thêm Công ty liên doanh mới</SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "20px" }}>
        <div>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages} onChange={(e) => setFieldValue("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate} onChange={(e) => setFieldValue("openingDate", e.target.value)} /></div>
      </div>

      {/* <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" }}>
        <div style={{ width: "30%" }}>
          <b>Người ghi sổ</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.ledgerClerk} onChange={(e) => setFieldValue("ledgerClerk", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <b>Kế toán trưởng</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.chiefAccountant} onChange={(e) => setFieldValue("chiefAccountant", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <div style={{ fontStyle: "italic", fontSize: "13px" }}>Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay} onChange={(e) => setFieldValue("signingDay", e.target.value)} /> tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth} onChange={(e) => setFieldValue("signingMonth", e.target.value)} /> năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear} onChange={(e) => setFieldValue("signingYear", e.target.value)} /></div>
          <b>Người đại diện theo pháp luật</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên, đóng dấu)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.legalRepresentative} onChange={(e) => setFieldValue("legalRepresentative", e.target.value)} /></SoftBox>
        </div>
      </div> */}
    </SoftBox>
  );
}
